import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, ShieldCheck, Users, Plane, Headphones } from 'lucide-react';

/**
 * Premium HeroSection — pixel-faithful to provided reference.
 *  - Right: airport runway at night photo (visible, with dotted world map + gold arcs)
 *  - Left: deep navy gradient blending into image for clean text area
 *  - Bottom: 4-cell stats wrapped in ONE rounded glass container with dividers
 *  - Animations: fade-in + slide-up only. No spinning.
 */
const HeroSection = () => {
  const stats = [
    { Icon: ShieldCheck, value: '10+', label: 'Years Experience' },
    { Icon: Users, value: '50+', label: 'Clients Served' },
    { Icon: Headphones, value: '24/7', label: 'Customer Support' },
  ];

  return (
    <>
    <section
      data-testid="hero-section"
      className="relative w-full min-h-screen flex flex-col justify-between overflow-hidden bg-[#0B1C2C] pt-24 pb-10"
    >
      {/* ============ BACKGROUND ============ */}
      {/* AI-generated runway/tower/terminal/sunset scene matching reference */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="/hero.png"
          alt=""
          aria-hidden="true"
          loading="eager"
          fetchpriority="high"
          className="absolute inset-[-50px_0px] w-full h-full object-cover"
          style={{
            objectPosition: 'center center',
            filter: 'brightness(0.95) saturate(1.05) contrast(1.05)'
          }}
        />
      </div>

      {/* Navy left → transparent right gradient (text area readability) */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B1C2C] via-[#0B1C2C]/85 to-transparent md:via-[#0B1C2C]/55"></div>
      {/* Bottom navy fade for stats card legibility */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0B1C2C] via-[#0B1C2C]/60 to-transparent"></div>
      {/* Top fade so header logo sits cleanly on navy */}
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#0B1C2C] via-[#0B1C2C]/85 to-transparent"></div>

      {/* Dotted world map + gold arcs — confined to upper-right (sky) area like reference */}
      {/* <div className="absolute top-0 right-0 w-full md:w-[70%] h-[60%] pointer-events-none hidden sm:block opacity-90">
        <WorldMapWithArcs />
      </div> */}

      {/* Soft gold glow accent */}
      <div className="absolute top-1/3 right-1/4 w-[26rem] h-[26rem] bg-[#D4AF37]/[0.08] rounded-full blur-[120px]"></div>

      {/* ============ HERO CONTENT ============ */}
      <div className="relative z-10 max-w-8xl mx-auto px-6 sm:px-8 lg:px-12 w-full flex-1 flex items-center">
        <div
          className="max-w-5xl opacity-0 animate-fade-slide-up text-center md:text-left mx-auto md:mx-0"
          style={{ animationFillMode: 'forwards' }}
          data-testid="hero-content"
        >
          {/* Eyebrow */}
          {/* <p className="text-[#D4AF37] text-[11px] sm:text-sm font-medium tracking-[0.22em] mb-5 uppercase">
            Welcome to Turbowing Aviation
          </p> */}

          {/* Headline — responsive sizes prevent overflow on small screens */}
          <h1 className="mt-5 text-[35px] sm:text-5xl md:text-7xl lg:text-[70px] xl:text-[88px] font-black leading-[1.05] mb-6 tracking-tight break-words">
            <span className="text-white block mb-3">Advancing Aviation, </span>
            <span
              className="text-[#D4AF37] block"
              style={{ textShadow: '0 0 40px rgba(212,175,55,0.35)' }}
            >
              Defining Excellence
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-white/85 text-sm sm:text-base md:text-lg leading-relaxed mb-10 sm:mb-12 max-w-md mx-auto md:mx-0 font-light">
            Delivering premium aviation solutions with precision, performance, and reliability.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-5 w-full sm:w-auto">
            <a href="/#services" data-testid="hero-cta-services" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto bg-[#D4AF37] hover:bg-[#e5c04d] text-[#0B1C2C] font-bold px-8 py-6 text-sm uppercase tracking-wider rounded-sm transition-all duration-500 hover:shadow-[0_0_35px_rgba(212,175,55,0.5)] hover:-translate-y-0.5 group">
                <span className="flex items-center justify-center">
                  Explore Services
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-500" />
                </span>
              </Button>
            </a>
            <a href="/contact" data-testid="hero-cta-contact" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto border-2 border-[#D4AF37] bg-transparent text-[#D4AF37] hover:bg-[#D4AF37]/10 font-semibold px-8 py-6 text-sm uppercase tracking-wider rounded-sm transition-all duration-500 hover:-translate-y-0.5 group">
                <span className="flex items-center justify-center">
                  Contact Us
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-500" />
                </span>
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* ============ STATS — ONE ROUNDED GLASS CONTAINER, 4 CELLS WITH DIVIDERS ============ */}
      

      <style jsx>{`
        @keyframes fade-slide-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-slide-up {
          animation: fade-slide-up 1.1s cubic-bezier(0.22, 1, 0.36, 1);
        }
      `}</style>
    </section>
    <section
      className="relative w-full bg-[#0B1C2C] flex items-center justify-center pt-0 pb-0 mt-0 md:mt-6 lg:mt-[-1.5rem] min-h-[0]"
      style={{ minHeight: 0 }}
    >
      <div
        className="relative z-20 max-w-5xl mx-auto px-4 sm:px-8 lg:px-12 w-full mt-8 sm:mt-12 md:mt-5 lg:mt-0"
      >
        <div
          className="opacity-0 animate-fade-slide-up rounded-2xl border border-white/10 backdrop-blur-xl bg-[#0B1C2C]/55 shadow-[0_10px_60px_rgba(0,0,0,0.45)]"
          style={{
            animationDelay: '0.4s',
            animationFillMode: 'forwards',
            maxWidth: '100%',
          }}
          data-testid="hero-stats"
        >
          <div className="grid grid-cols-2 md:grid-cols-3">
            {stats.map(({ Icon, value, label }, i) => (
              <div
                key={i}
                data-testid={`hero-stat-${i}`}
                className={`relative flex flex-col items-center text-center px-4 py-5 sm:py-5 transition-all duration-500 hover:bg-white/[0.04] group
                  ${i < stats.length - 1 ? 'md:after:content-[""] md:after:absolute md:after:right-0 md:after:top-1/4 md:after:h-1/2 md:after:w-px md:after:bg-white/10' : ''}
                  ${i % 2 === 0 ? 'after:content-[""] after:absolute after:right-0 after:top-1/4 after:h-1/2 after:w-px after:bg-white/10 md:after:bg-white/10' : ''}
                  ${i < 2 ? 'border-b border-white/10 md:border-b-0' : ''}
                `}
              >
                <Icon
                  className="w-11 h-11 sm:w-12 sm:h-12 mb-4 text-[#D4AF37] transition-transform duration-500 group-hover:scale-110"
                  strokeWidth={1.5}
                />
                <div
                  className="text-5xl sm:text-6xl md:text-[55px] font-black text-[#D4AF37] mb-2 tracking-tight leading-none"
                  style={{ textShadow: '0 0 24px rgba(212,175,55,0.35)' }}
                >
                  {value}
                </div>
                <div className="text-xs sm:text-sm md:text-base text-white/75 font-medium tracking-wide">
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
      </>
  );
};

/**
 * Dotted-world-map silhouette + thin gold curved arcs with small plane markers.
 * Pure SVG, no animation. Continents are filled with a gold dot pattern that
 * naturally gets clipped to the continent shape.
 */
const WorldMapWithArcs = () => (
  <svg
    viewBox="0 0 1000 600"
    preserveAspectRatio="xMidYMid slice"
    className="w-full h-full"
    aria-hidden="true"
  >
    <defs>
      <pattern id="goldDots" width="5" height="5" patternUnits="userSpaceOnUse">
        <circle cx="2.5" cy="2.5" r="0.95" fill="#D4AF37" opacity="0.65" />
      </pattern>
      <linearGradient id="arcGold" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#D4AF37" stopOpacity="0" />
        <stop offset="35%" stopColor="#D4AF37" stopOpacity="0.85" />
        <stop offset="65%" stopColor="#D4AF37" stopOpacity="0.85" />
        <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
      </linearGradient>
      <radialGradient id="hubGlow">
        <stop offset="0%" stopColor="#D4AF37" stopOpacity="1" />
        <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
      </radialGradient>
    </defs>

    {/* Continents filled with gold dot pattern (creates dotted world map look) */}
    <g fill="url(#goldDots)">
      {/* North America */}
      <path d="M80,140 Q120,90 200,90 Q260,95 295,135 Q310,180 280,215 Q235,235 185,225 Q135,215 100,195 Q70,175 80,140 Z" />
      {/* Central / South America */}
      <path d="M260,250 Q295,260 305,295 Q310,345 295,400 Q278,450 250,455 Q235,420 245,375 Q255,330 250,290 Q255,260 260,250 Z" />
      {/* Europe */}
      <path d="M455,100 Q510,90 555,105 Q585,125 580,150 Q560,175 520,180 Q485,178 460,160 Q445,135 455,100 Z" />
      {/* Africa */}
      <path d="M475,180 Q540,180 575,205 Q595,250 588,300 Q575,360 540,395 Q510,405 490,380 Q470,335 470,285 Q468,235 475,180 Z" />
      {/* Middle East */}
      <path d="M580,180 Q625,180 645,210 Q640,240 610,245 Q585,235 575,210 Q575,190 580,180 Z" />
      {/* Asia (Russia/China) */}
      <path d="M615,95 Q720,80 800,100 Q870,120 905,160 Q900,200 855,225 Q780,235 700,225 Q645,210 620,180 Q600,145 615,95 Z" />
      {/* India */}
      <path d="M675,225 Q720,225 730,255 Q720,295 695,305 Q670,295 660,265 Q660,240 675,225 Z" />
      {/* SE Asia / Indonesia */}
      <path d="M780,265 Q840,265 880,290 Q880,315 840,325 Q790,320 770,300 Q765,275 780,265 Z" />
      {/* Australia */}
      <path d="M810,375 Q885,370 925,395 Q925,430 880,445 Q830,445 800,425 Q790,395 810,375 Z" />
      {/* Greenland small */}
      <path d="M380,90 Q415,80 430,100 Q425,125 400,130 Q380,118 380,90 Z" />
    </g>

    {/* Gold connection arcs across the map */}
    <g fill="none" stroke="url(#arcGold)" strokeWidth="1.4" opacity="0.95">
      <path d="M180,160 Q400,40 700,180" />
      <path d="M700,180 Q820,90 920,200" />
      <path d="M180,160 Q450,300 540,340" />
      <path d="M610,210 Q780,260 890,400" />
      <path d="M280,400 Q500,320 700,180" />
      <path d="M520,150 Q640,80 800,140" />
    </g>

    {/* Subtle dashed flight paths (no animation) */}
    <g
      fill="none"
      stroke="#D4AF37"
      strokeWidth="0.8"
      strokeDasharray="2 5"
      opacity="0.35"
    >
      <path d="M180,160 Q400,40 700,180" />
      <path d="M700,180 Q820,90 920,200" />
      <path d="M280,400 Q500,320 700,180" />
    </g>

    {/* Tiny plane markers along arcs */}
    <g fill="#D4AF37">
      <PlaneMark x={500} y={95} rotate={20} />
      <PlaneMark x={820} y={130} rotate={35} />
      <PlaneMark x={500} y={260} rotate={-25} />
      <PlaneMark x={780} y={290} rotate={55} />
    </g>

    {/* Hub glow points */}
    <g>
      {[
        [180, 160],
        [520, 150],
        [700, 180],
        [890, 200],
        [540, 340],
        [890, 400],
        [280, 400],
      ].map(([cx, cy], idx) => (
        <g key={idx}>
          <circle cx={cx} cy={cy} r="9" fill="url(#hubGlow)" opacity="0.85" />
          <circle cx={cx} cy={cy} r="2" fill="#D4AF37" />
        </g>
      ))}
    </g>
  </svg>
);

const PlaneMark = ({ x, y, rotate = 0 }) => (
  <g transform={`translate(${x} ${y}) rotate(${rotate})`}>
    <path d="M0,-6 L1.2,-1 L7,0 L1.2,1 L0,6 L-1.2,1 L-7,0 L-1.2,-1 Z" />
  </g>
);

export default HeroSection;
